import type { ToastAction } from '@reducers/toast';

export interface BillingErrorCopy {
  title: string;
  message: string;
  action?: ToastAction;
}

/**
 * Backend error codes that mean "billing / upstream provider", not "something
 * broke". They are typed server-side (decision 9109) precisely so the UI can
 * tell an empty vendor balance apart from a crash — before this, a HikerAPI
 * `402` arrived as `INTERNAL_ERROR` and read as a broken product.
 *
 * Only `INSUFFICIENT_CREDITS` / `INSUFFICIENT_BALANCE` are actionable by the
 * user; the provider ones are ours to fix, so their copy says so instead of
 * asking the user to retry forever.
 *
 * Shared by the global Apollo toast and inline surfaces (AI chat banner).
 */
export const BILLING_ERROR_COPY: Record<string, BillingErrorCopy> = {
  // Our own paywall (server credit_guard): the org's credit balance can't cover the op.
  INSUFFICIENT_CREDITS: {
    title: 'Out of credits',
    message:
      'This action needs more credits than your workspace has. Everything you already found stays available.',
    action: { label: 'Buy credits', to: '/settings/organization/billing' },
  },
  INSUFFICIENT_BALANCE: {
    title: 'Out of credits',
    message:
      'Add credits to keep fetching fresh data. Everything already indexed stays available.',
  },
  PROVIDER_QUOTA_EXHAUSTED: {
    title: 'Live data paused',
    message:
      'Fresh Instagram data is temporarily unavailable — we are on it. Indexed creators and posts are unaffected.',
  },
  PROVIDER_UNAVAILABLE: {
    title: 'Live data delayed',
    message:
      'The data provider is slow or unreachable right now. Indexed data still works; try the refresh again shortly.',
  },
};

export const getBillingErrorCopy = (
  code?: string | null,
): BillingErrorCopy | null =>
  code && Object.prototype.hasOwnProperty.call(BILLING_ERROR_COPY, code)
    ? BILLING_ERROR_COPY[code]
    : null;

/** First billing code among an Apollo error's GraphQL errors, else null. */
export const getBillingErrorCode = (error: unknown): string | null => {
  const graphQLErrors = (
    error as {
      graphQLErrors?: ReadonlyArray<{ extensions?: { code?: unknown } }>;
    } | null
  )?.graphQLErrors;
  const code = graphQLErrors
    ?.map((e) => String(e?.extensions?.code ?? ''))
    .find((c) => getBillingErrorCopy(c));
  return code ?? null;
};
